import { Stack } from '@mui/material';
import { type Dispatch, type SetStateAction, useCallback } from 'react';
import { shallow } from 'zustand/shallow';
import { ListOptions } from './ListOptions';
import type { SortOption, sortByStoreProps } from './types';

type Props<TSortValue, TFilterOptions extends string> = {
  isOpen: boolean;
  closeDropdown: () => void;
  activeIndex: number;
  setActiveIndex: Dispatch<SetStateAction<number>>;
  scrollBackToMainBar?: () => void;
  sortOptions: readonly SortOption<TSortValue>[];
  useStore: sortByStoreProps<TSortValue, TFilterOptions>;
  sortByLabel: string;
  sortBySelectEvent?: (
    overrideProps?: Record<string, string | object> | null,
    callback?: (() => void) | null,
  ) => undefined;
};

export const ListBox = <TSortValue extends string, TFilterOptions extends string>({
  isOpen,
  closeDropdown,
  activeIndex,
  setActiveIndex,
  scrollBackToMainBar,
  sortOptions,
  useStore,
  sortByLabel,
  sortBySelectEvent,
}: Props<TSortValue, TFilterOptions>) => {
  const { handleSortBy } = useStore(
    ({ handleSortBy }) => ({
      handleSortBy,
    }),
    shallow,
  );

  const handleSelectSortBy = useCallback(
    (sortValue: TSortValue) => {
      closeDropdown();

      const selectedOption = sortOptions.find(option => option.value === sortValue);
      if (sortBySelectEvent && selectedOption) {
        sortBySelectEvent({
          additional_attributes: {
            sort_by_value: selectedOption.label,
          },
        });
      }

      setTimeout(() => {
        handleSortBy(sortValue);
      }, 0);

      if (scrollBackToMainBar) {
        scrollBackToMainBar();
      }
    },
    [handleSortBy, sortOptions, sortBySelectEvent, closeDropdown, scrollBackToMainBar],
  );

  const moveOnList = (directionMove: 1 | -1) =>
    setActiveIndex(index => (index + directionMove + sortOptions.length) % sortOptions.length);

  const onKeyList = (e: React.KeyboardEvent) => {
    e.preventDefault();
    switch (e.key) {
      case 'ArrowDown':
        moveOnList(1);
        break;
      case 'ArrowUp':
        moveOnList(-1);
        break;
      case 'Home':
        setActiveIndex(0);
        break;
      case 'End':
        setActiveIndex(sortOptions.length - 1);
        break;
    }
  };

  return (
    <Stack
      id="sort-by-dropdown-list"
      role="listbox"
      aria-labelledby="sort-by-dropdown-button"
      tabIndex={0}
      sx={{ py: 0.5 }}
      onKeyDown={onKeyList}
    >
      <ListOptions
        isOpen={isOpen}
        activeIndex={activeIndex}
        handleSelectSortBy={handleSelectSortBy}
        sortOptions={sortOptions}
        useStore={useStore}
        sortByLabel={sortByLabel}
      />
    </Stack>
  );
};
